import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ProcessHeader from "components/ui/ProcessHeader";
import Input from "components/ui/Input";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import { useChat } from "../../hooks/useChat";
import toast from "react-hot-toast";
import BlueprintPaymentModal from "./components/BlueprintPaymentModal";
import { supabase } from "../../lib/supabase";

const INTEREST_OPTIONS = [
"Mentoring & Coaching", "Writing & Content", "Teaching & Training",
"Consulting & Advisory", "Problem Solving", "Process Improvement",
"Leadership Development", "Technology & Innovation", "Healthcare",
"Finance & Investing", "Legal & Compliance", "Marketing & Sales",
"Operations & Logistics", "Human Resources", "Real Estate"];


const ProfessionalInputForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    profession: "",
    email: "",
    experience: "",
    skills: "",
    interests: [],
    knowledge: "",
    problems: ""
  });
  const [errors, setErrors] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const progressIntervalRef = useRef(null);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [hasPaid, setHasPaid] = useState(() => {
    return sessionStorage.getItem('quantume_blueprint_paid') === 'true';
  });
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentIntentId, setPaymentIntentId] = useState(() => {
    return sessionStorage.getItem('quantume_payment_intent_id') || null;
  });

  // Generate or retrieve a persistent session ID for this browser session
  const [sessionId] = useState(() => {
    const existing = sessionStorage.getItem('quantume_session_id');
    if (existing) return existing;
    const newId = typeof crypto !== 'undefined' && crypto?.randomUUID ?
    crypto.randomUUID() :
    `session_${Date.now()}_${Math.random()?.toString(36)?.slice(2)}`;
    sessionStorage.setItem('quantume_session_id', newId);
    return newId;
  });

  const { response, isLoading, error: aiError, sendMessage } = useChat('OPEN_AI', 'gpt-4.1', false);

  useEffect(() => {
    if (aiError) {
      toast?.error(aiError?.message || 'Unable to generate blueprint. Please check your connection and try again.');
      setIsGenerating(false);
      setPendingFormData(null);
    }
  }, [aiError]);

  useEffect(() => {
    if (!isLoading && response && pendingFormData && !aiError) {
      handleBlueprintReady(response, pendingFormData);
    }
  }, [isLoading, response, aiError]);

  useEffect(() => {
    if (isGenerating) {
      setProgress(0);
      const totalMs = 25000;
      const targetPct = 90;
      const intervalMs = 300;
      const steps = totalMs / intervalMs;
      const increment = targetPct / steps;
      progressIntervalRef.current = setInterval(() => {
        setProgress((prev) => {
          const next = prev + increment;
          if (next >= targetPct) {
            clearInterval(progressIntervalRef?.current);
            return targetPct;
          }
          return next;
        });
      }, intervalMs);
    } else {
      clearInterval(progressIntervalRef?.current);
      if (progress > 0) {
        setProgress(100);
        const resetTimer = setTimeout(() => setProgress(0), 600);
        return () => clearTimeout(resetTimer);
      }
    }
    return () => clearInterval(progressIntervalRef?.current);
  }, [isGenerating]);

  const handleBlueprintReady = async (rawResponse, formDataSnapshot) => {
    try {
      let blueprint = null;
      let rawJson = null;

      // Strategy 1: Extract from ```json ... ``` code block
      const jsonMatch = rawResponse?.match(/```json\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        rawJson = jsonMatch?.[1];
      }

      // Strategy 2: Extract from ``` ... ``` code block (no language tag)
      if (!rawJson) {
        const codeMatch = rawResponse?.match(/```\s*([\s\S]*?)\s*```/);
        if (codeMatch) rawJson = codeMatch?.[1];
      }

      // Strategy 3: Find raw JSON object in the response
      if (!rawJson) {
        const jsonObjectMatch = rawResponse?.match(/\{[\s\S]*\}/);
        if (jsonObjectMatch) rawJson = jsonObjectMatch?.[0];
      }

      // Strategy 4: Use the full response as-is
      if (!rawJson) rawJson = rawResponse;

      // Check if response is HTML instead of JSON (API error page)
      if (!rawJson || rawJson?.trim()?.startsWith('<') || rawJson?.trim()?.toLowerCase()?.startsWith('<!doctype')) {
        throw new Error('AI service is currently unavailable. Please try again later.');
      }

      blueprint = JSON.parse(rawJson);

      // Mark that a blueprint has been generated for this payment
      sessionStorage.setItem('quantume_blueprint_generated', 'true');

      setIsGenerating(false);
      setPendingFormData(null);
      navigate("/prosperous-income-blueprint", { state: { formData: formDataSnapshot, blueprint } });
    } catch (err) {
      console.error('Blueprint processing error:', err);
      const isConfigError = err?.message?.includes('not configured') || err?.message?.includes('invalid response') || err?.message?.includes('unavailable');
      toast?.error(isConfigError ?
      'AI service is currently unavailable. Please try again later.' : 'Error processing blueprint. Please try again.');
      setIsGenerating(false);
      setPendingFormData(null);
    }
  };

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e?.target?.value }));
    if (errors?.[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const toggleInterest = (interest) => {
    setFormData((prev) => ({
      ...prev,
      interests: prev?.interests?.includes(interest) ?
      prev?.interests?.filter((i) => i !== interest) :
      [...prev?.interests, interest]
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData?.name?.trim()) newErrors.name = "Please enter your name.";
    if (!formData?.profession?.trim()) newErrors.profession = "Please describe your knowledge, education, and experience.";
    if (!formData?.email?.trim()) {
      newErrors.email = "Please enter your email address.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = "Please enter a valid email address.";
    }
    if (!formData?.experience?.trim()) newErrors.experience = "Please share some of your life experience.";
    if (!formData?.skills?.trim()) newErrors.skills = "Please list your key skills.";
    if (formData?.interests?.length === 0) newErrors.interests = "Please select at least one area of interest.";
    if (!formData?.problems?.trim()) newErrors.problems = "Please describe problems you enjoy solving.";
    return newErrors;
  };

  const handleGenerate = () => {
    if (!hasPaid) {
      toast?.error('Payment required. Please complete your $22 payment to generate your blueprint.');
      setShowPaymentModal(true);
      return;
    }
    const validationErrors = validate();
    if (Object.keys(validationErrors)?.length > 0) {
      setErrors(validationErrors);
      const firstErrorKey = Object.keys(validationErrors)?.[0];
      const el = document.getElementById(firstErrorKey);
      if (el) el?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    // Check if a blueprint has already been generated for this payment
    const alreadyGenerated = sessionStorage.getItem('quantume_blueprint_generated') === 'true';
    if (alreadyGenerated) {
      toast?.error(
        "You've already generated your blueprint for this purchase. Each payment allows one blueprint generation.",
        { duration: 6000 }
      );
      return;
    }

    setIsGenerating(true);
    setPendingFormData({ ...formData });

    const prompt = `You are an expert income strategist and career coach. Based on the following professional profile, generate a comprehensive Prosperous Income Blueprint as a JSON object.

PROFILE:
- Name: ${formData?.name}
- Field/Profession: ${formData?.profession}
- Email Address: ${formData?.email || 'Not specified'}
- Life & Career Experience: ${formData?.experience}
- Key Skills: ${formData?.skills}
- Areas of Interest: ${formData?.interests?.join(', ')}
- Knowledge Domains: ${formData?.knowledge || 'Not specified'}
- Problems They Solve: ${formData?.problems}

Generate a JSON object with EXACTLY this structure (no extra text, just the JSON):
{
  "hiddenValueAssets": [
    { "icon": "Brain", "title": "string", "description": "string" },
    { "icon": "Network", "title": "string", "description": "string" },
    { "icon": "Shield", "title": "string", "description": "string" },
    { "icon": "Lightbulb", "title": "string", "description": "string" },
    { "icon": "MessageSquare", "title": "string", "description": "string" },
    { "icon": "TrendingUp", "title": "string", "description": "string" }
  ],
  "aiOpportunities": [
    { "title": "string", "incomeModel": "string", "whoItServes": "string", "howAIHelps": "string", "estimatedIncome": "string" },
    { "title": "string", "incomeModel": "string", "whoItServes": "string", "howAIHelps": "string", "estimatedIncome": "string" },
    { "title": "string", "incomeModel": "string", "whoItServes": "string", "howAIHelps": "string", "estimatedIncome": "string" }
  ],
  "recommendedPath": {
    "title": "string",
    "description": "string",
    "whyItFits": "string",
    "howItGeneratesIncome": "string",
    "feasibility": "string"
  },
  "launchWeeks": [
    { "theme": "string", "tasks": ["string", "string", "string", "string"] },
    { "theme": "string", "tasks": ["string", "string", "string", "string"] },
    { "theme": "string", "tasks": ["string", "string", "string", "string"] },
    { "theme": "string", "tasks": ["string", "string", "string", "string"] }
  ],
  "firstThreeSteps": [
    { "title": "string", "description": "string", "timeEstimate": "string" },
    { "title": "string", "description": "string", "timeEstimate": "string" },
    { "title": "string", "description": "string", "timeEstimate": "string" }
  ]
}

Make all content highly specific to this person's actual background, skills, and interests. Be concrete, actionable, and encouraging. Return ONLY the JSON object.`;

    sendMessage([
    { role: 'system', content: 'You are an expert income strategist. Always respond with valid JSON only, no markdown, no explanation.' },
    { role: 'user', content: prompt }],
    { max_completion_tokens: 3000 });
  };

  const handlePaymentSuccess = (paymentIntent, collectedInfo) => {
    const intentId = paymentIntent?.id || null;
    setPaymentIntentId(intentId);
    if (intentId) {
      sessionStorage.setItem('quantume_payment_intent_id', intentId);
    }
    sessionStorage.setItem('quantume_blueprint_paid', 'true');
    // Reset the generated flag for the new payment
    sessionStorage.removeItem('quantume_blueprint_generated');
    setHasPaid(true);
    setShowPaymentModal(false);

    // Pre-fill form fields with info collected during checkout if not already filled
    if (collectedInfo) {
      setFormData((prev) => ({
        ...prev,
        name: prev?.name?.trim() || collectedInfo?.name || prev?.name,
        email: prev?.email?.trim() || collectedInfo?.email || prev?.email
      }));
    }

    toast?.success('Payment successful! You can now generate your Prospectus Income Blueprint.');
  };

  // Check for ?paid=paymentIntentId in URL and verify against Supabase
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paidParam = params?.get('paid');
    if (!paidParam) return;

    // Already verified in this session
    if (sessionStorage.getItem('quantume_blueprint_paid') === 'true' &&
    sessionStorage.getItem('quantume_payment_intent_id') === paidParam) {
      return;
    }

    const verifyPayment = async () => {
      try {
        const { data, error } = await supabase?.from('blueprint_payments')?.select('id, payment_status, payment_intent_id')?.eq('payment_intent_id', paidParam)?.eq('payment_status', 'succeeded')?.maybeSingle();

        if (!error && data) {
          sessionStorage.setItem('quantume_blueprint_paid', 'true');
          sessionStorage.setItem('quantume_payment_intent_id', paidParam);
          setHasPaid(true);
          setPaymentIntentId(paidParam);
          toast?.success('Payment verified! You can now generate your Prospectus Income Blueprint.');
        } else {
          console.warn('Payment verification failed for:', paidParam, error);
        }
      } catch (err) {
        console.error('Error verifying payment from URL:', err);
      }
    };

    verifyPayment();
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--color-background)" }}>
      <ProcessHeader completedSteps={[]} />
      {/* Generating Overlay */}
      {isGenerating &&
      <div
        className="fixed inset-0 z-50 flex items-center justify-center"
        style={{ backgroundColor: "rgba(255,248,237,0.96)", backdropFilter: "blur(4px)" }}>

          <div className="flex flex-col items-center text-center px-8 max-w-md">
            {/* Spinner */}
            <div className="mb-6 relative">
              <div
              className="w-16 h-16 rounded-full border-4 border-t-transparent animate-spin"
              style={{ borderColor: "rgba(40,59,163,0.2)", borderTopColor: "#283ba3" }} />

              <div
              className="absolute inset-0 w-16 h-16 rounded-full border-4 border-b-transparent animate-spin"
              style={{ borderColor: "transparent", borderBottomColor: "#d3ac35", animationDuration: "1.4s", animationDirection: "reverse" }} />

            </div>

            {/* Heading */}
            <h2
            className="text-xl md:text-2xl font-bold mb-3 leading-snug"
            style={{ fontFamily: "var(--font-heading)", color: "#283ba3" }}>

              Generating Your QuantuMe{" "}
              <span style={{ color: "#d3ac35" }}>Prospectus Blueprint…</span>
            </h2>

            {/* Subtext */}
            <p
            className="text-sm mb-5"
            style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-body)" }}>

              Please wait while we analyze:
            </p>

            {/* Animated bullet points */}
            <ul className="space-y-3 text-left">
              {[
            "Your expertise",
            "Your experience",
            "Your skills",
            "Emerging AI-powered opportunity paths"]?.
            map((item, i) =>
            <li
              key={item}
              className="flex items-center gap-3 text-sm font-medium animate-pulse"
              style={{
                color: "#283ba3",
                animationDelay: `${i * 0.3}s`,
                animationDuration: "1.8s",
                fontFamily: "var(--font-body)"
              }}>

                  <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: "#d3ac35" }} />

                  {item}
                </li>
            )}
            </ul>

            {/* Progress Bar */}
            <div className="w-full mt-6">
              <div className="flex justify-between items-center mb-1.5">
                <span
                className="text-xs font-medium"
                style={{ color: "#283ba3", fontFamily: "var(--font-body)" }}>

                  {progress < 90 ? "Analyzing your profile…" : progress < 100 ? "Finalizing blueprint…" : "Complete!"}
                </span>
                <span
                className="text-xs font-bold"
                style={{ color: "#d3ac35", fontFamily: "var(--font-body)" }}>

                  {Math.round(progress)}%
                </span>
              </div>
              <div
              className="w-full rounded-full overflow-hidden"
              style={{ height: "8px", backgroundColor: "rgba(40,59,163,0.12)" }}>

                <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${progress}%`,
                  background: "linear-gradient(90deg, #283ba3 0%, #d3ac35 100%)",
                  transition: "width 0.3s ease-out"
                }} />

              </div>
            </div>
          </div>
        </div>
      }
      <main className="pt-16">
        <div className="max-w-3xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-10 lg:py-12 pb-[25px]">

          {/* Hero Section */}
          <div className="mb-8 md:mb-10 rounded-2xl px-6 py-10 md:px-10 md:py-4 text-center rounded-br-2xl rounded-t-2xl rounded-bl-2xl pb-0" style={{ background: "linear-gradient(135deg, rgba(40,59,163,0.08) 0%, rgba(211,172,53,0.10) 100%)", border: "1px solid rgba(40,59,163,0.12)" }}>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold leading-tight mt-4 mb-1" style={{ fontFamily: "var(--font-heading)", color: "var(--color-foreground)", fontWeight: 700 }}>
              Turn Your Skills Into a{" "}
              <span style={{ color: "var(--color-primary)" }}>QuantuMe</span>{" "}
              <span style={{ color: "var(--color-accent)" }}>Prospectus </span>
               Income Blueprint
            </h1>
          </div>

          {/* Benefits Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8 md:mb-10">
            {/* Card 1: Your Hidden Value Assets */}
            <div className="rounded-2xl p-6 flex flex-col" style={{ backgroundColor: "#fffbf5", border: "1.5px solid rgba(40,59,163,0.18)", boxShadow: "0 2px 16px rgba(40,59,163,0.07)" }}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(40,59,163,0.10)" }}>
                  <span className="text-sm font-bold" style={{ color: "#283ba3" }}>1</span>
                </div>
                <h3 className="text-base font-bold leading-tight" style={{ fontFamily: "var(--font-heading)", color: "#283ba3", fontWeight: 700 }}>
                  Your Hidden Value Assets
                </h3>
              </div>
              <ul className="flex flex-col gap-2.5">
                {["Your Core strengths", "Your transferable skills", "Experience patterns", "Problems you are positioned to solve"]?.map((item) =>
                <li key={item} className="flex items-start gap-2">
                    <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: "#d3ac35" }}></span>
                    <span className="text-sm" style={{ fontFamily: "var(--font-body)", color: "#374151", lineHeight: "1.6" }}>{item}</span>
                  </li>
                )}
              </ul>
            </div>

            {/* Card 2: 3 Ways AI Can Multiply Your Skills */}
            <div className="rounded-2xl p-6 flex flex-col" style={{ backgroundColor: "#fffbf5", border: "1.5px solid rgba(40,59,163,0.18)", boxShadow: "0 2px 16px rgba(211,172,53,0.10)" }}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(40,59,163,0.10)" }}>
                  <span className="text-sm font-bold" style={{ color: "#283ba3" }}>2</span>
                </div>
                <h3 className="text-base font-bold leading-tight" style={{ fontFamily: "var(--font-heading)", color: "#283ba3", fontWeight: 700 }}>
                  3 Ways AI Can Multiply Skills
                </h3>
              </div>
              <ul className="flex flex-col gap-2.5">
                {["Best income model", "Who you serve", "How AI helps you work faster or scale"]?.map((item) =>
                <li key={item} className="flex items-start gap-2">
                    <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: "#d3ac35" }}></span>
                    <span className="text-sm" style={{ fontFamily: "var(--font-body)", color: "#374151", lineHeight: "1.6" }}>{item}</span>
                  </li>
                )}
              </ul>
            </div>

            {/* Card 3: Recommended Starting Path */}
            <div className="rounded-2xl p-6 flex flex-col" style={{ backgroundColor: "#fffbf5", border: "1.5px solid rgba(40,59,163,0.18)", boxShadow: "0 2px 16px rgba(40,59,163,0.07)" }}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(40,59,163,0.10)" }}>
                  <span className="text-sm font-bold" style={{ color: "#283ba3" }}>3</span>
                </div>
                <h3 className="text-base font-bold leading-tight" style={{ fontFamily: "var(--font-heading)", color: "#283ba3", fontWeight: 700 }}>
                  Recommended Starting Path
                </h3>
              </div>
              <ul className="flex flex-col gap-2.5">
                {["Why it fits you", "How it generates income", "Why it's realistic to start within 30 days"]?.map((item) =>
                <li key={item} className="flex items-start gap-2">
                    <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: "#d3ac35" }}></span>
                    <span className="text-sm" style={{ fontFamily: "var(--font-body)", color: "#374151", lineHeight: "1.6" }}>{item}</span>
                  </li>
                )}
              </ul>
            </div>
          </div>

          {/* Payment CTA — above Tell Us About You */}
          {!hasPaid &&
          <div
            className="mb-8 md:mb-10 rounded-2xl p-6 md:p-8"
            style={{
              background: "linear-gradient(135deg, rgba(40,59,163,0.08) 0%, rgba(211,172,53,0.12) 100%)",
              border: "2px solid rgba(211,172,53,0.40)",
              boxShadow: "0 4px 24px rgba(40,59,163,0.10)"
            }}>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span
                    className="text-xs font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: "rgba(211,172,53,0.18)", color: "#d3ac35", fontFamily: "var(--font-caption)" }}>

                      One-Time Payment
                    </span>
                  </div>
                  <h2
                  className="text-lg md:text-xl font-bold mb-1"
                  style={{ fontFamily: "var(--font-heading)", color: "#283ba3", fontWeight: 700 }}>

                    Unlock Your QuantuMe Prospectus Blueprint
                  </h2>
                  <p className="text-sm mb-3" style={{ color: "#6b7280", fontFamily: "var(--font-body)", maxWidth: "52ch" }}>
                    Get your personalized AI-powered income strategy — your Hidden Value Assets, 3 income paths, a 4-week launch plan, and your first 3 steps to start today.
                  </p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold" style={{ color: "#283ba3", fontFamily: "var(--font-heading)", fontWeight: 700 }}>$22</span>
                    <span className="text-sm" style={{ color: "#9ca3af", fontFamily: "var(--font-caption)" }}>one-time · instant access</span>
                  </div>
                </div>
                <button
                onClick={() => setShowPaymentModal(true)}
                className="flex-shrink-0 flex items-center gap-2 px-6 py-3.5 rounded-xl font-bold text-sm transition-all hover:opacity-90 active:scale-95"
                style={{
                  background: "linear-gradient(135deg, #283ba3 0%, #1e2d8a 100%)",
                  color: "#fff",
                  fontFamily: "var(--font-heading)",
                  fontWeight: 700,
                  boxShadow: "0 4px 16px rgba(40,59,163,0.30)",
                  border: "2px solid #d3ac35",
                  letterSpacing: "0.01em"
                }}>

                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d3ac35" strokeWidth="2.2">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                    <line x1="1" y1="10" x2="23" y2="10" />
                  </svg>
                  Unlock Blueprint — $22
                </button>
              </div>
            </div>
          }

          {hasPaid &&
          <div
            className="mb-8 md:mb-10 rounded-2xl px-5 py-4 flex items-center gap-3"
            style={{
              background: "linear-gradient(135deg, rgba(40,59,163,0.06) 0%, rgba(211,172,53,0.08) 100%)",
              border: "1.5px solid rgba(211,172,53,0.35)"
            }}>

              <span
              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "#d3ac35" }}>

                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M5 13l4 4L19 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <div>
                <p className="text-sm font-bold" style={{ color: "#283ba3", fontFamily: "var(--font-heading)", fontWeight: 700 }}>Payment Successful!</p>
                <p className="text-xs text-right ml-0 pl-0" style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-caption)" }}>Complete the form below to generate your personalized blueprint.</p>
              </div>
            </div>
          }

          {/* Page Header */}
          <div className="mb-8 md:mb-10">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(211,172,53,0.15)", color: "var(--color-accent)", fontFamily: "var(--font-caption)" }}>Step 1 of 2</span>
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2" style={{ fontFamily: "var(--font-heading)", color: "var(--color-foreground)", fontWeight: 700 }}>
              Tell Us About You!
            </h1>
            <p className="text-sm md:text-base" style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-body)", maxWidth: "60ch" }}>
              Your experience is your greatest asset. The more you share, the more personalized your income blueprint will be. Your data is not saved or stored.
            </p>
          </div>

          <div className="flex flex-col gap-6 md:gap-8">

            {/* Personal Info */}
            <div className="rounded-2xl border p-6 md:p-8" style={{ border: "1.5px solid rgba(40,59,163,0.18)", backgroundColor: "#fffbf5" }}>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: "rgba(40,59,163,0.08)" }}>
                  <Icon name="User" size={18} color="var(--color-primary)" strokeWidth={2} />
                </div>
                <h2 className="text-base md:text-lg font-bold" style={{ fontFamily: "var(--font-heading)", color: "var(--color-foreground)", fontWeight: 700 }}>About You</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div id="name">
                  <Input
                    label="Your Name"
                    type="text"
                    placeholder="e.g., Margaret Johnson"
                    value={formData?.name}
                    onChange={handleChange("name")}
                    error={errors?.name}
                    className="bg-amber-100"
                    required />
                </div>
                <Input
                  label="Email Address"
                  type="email"
                  placeholder="e.g., margaret@email.com"
                  value={formData?.email}
                  onChange={handleChange("email")}
                  error={errors?.email}
                  className="bg-amber-100"
                  required />
              </div>
            </div>

            {/* Professional Background */}
            <div className="bg-white rounded-2xl border p-6 md:p-8" style={{ border: "1.5px solid rgba(40,59,163,0.18)", backgroundColor: "#fffbf5" }}>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: "rgba(40,59,163,0.08)" }}>
                  <Icon name="Briefcase" size={18} color="var(--color-primary)" strokeWidth={2} />
                </div>
                <h2 className="text-base md:text-lg font-bold" style={{ fontFamily: "var(--font-heading)", color: "var(--color-foreground)", fontWeight: 700 }}>Your Knowledge, Education, Experience</h2>
              </div>
              <div className="flex flex-col gap-4">
                <div id="profession">
                  <Input
                    label="Your Field or Profession"
                    type="text"
                    placeholder="e.g., Indoor Plant Propagation, Healthcare Administration, Vintage Car Restoration"
                    value={formData?.profession}
                    onChange={handleChange("profession")}
                    error={errors?.profession}
                    className="bg-amber-100"
                    required />
                </div>
                <div id="experience">
                  <label className="block text-sm font-medium mb-1" style={{ fontFamily: "var(--font-heading)", color: "var(--color-foreground)", fontWeight: 700 }}>
                    Life &amp; Career Experience <span style={{ color: "var(--color-destructive)" }}>*</span>
                  </label>
                  <p className="mb-2 text-sm" style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-caption)" }}>
                    Describe your life experience, key roles, major accomplishments, and any training that shaped your expertise.
                  </p>
                  <textarea
                    className="w-full rounded-lg border px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 transition-all bg-amber-100"
                    style={{
                      borderColor: errors?.experience ? "var(--color-destructive)" : "var(--color-border)",
                      color: "var(--color-foreground)",
                      minHeight: "120px"
                    }}
                    placeholder="e.g., 22 years in hospital administration, including 8 years as a department director overseeing 150 staff and a $12M budget. Led a system-wide EHR implementation that reduced documentation errors by 40%..."
                    value={formData?.experience}
                    onChange={handleChange("experience")}
                    rows={5} />
                  {errors?.experience && <p className="text-xs mt-1" style={{ color: "var(--color-destructive)", fontFamily: "var(--font-caption)" }}>{errors?.experience}</p>}
                </div>
              </div>
            </div>

            {/* Skills */}
            <div className="bg-white rounded-2xl border p-6 md:p-8" style={{ border: "1.5px solid rgba(40,59,163,0.18)", backgroundColor: "#fffbf5" }}>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: "rgba(40,59,163,0.08)" }}>
                  <Icon name="Star" size={18} color="var(--color-primary)" strokeWidth={2} />
                </div>
                <h2 className="text-base md:text-lg font-bold" style={{ fontFamily: "var(--font-heading)", color: "var(--color-foreground)", fontWeight: 700 }}> Life Skills </h2>
              </div>
              <div className="flex flex-col gap-4">
                <div id="skills">
                  <label className="block font-medium mb-1 text-base" style={{ fontFamily: "var(--font-heading)", color: "var(--color-foreground)", fontWeight: 700 }}>
                    Your Key Skills <span style={{ color: "var(--color-destructive)" }}>*</span>
                  </label>
                  <p className="mb-2 text-sm" style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-caption)" }}>
                    List skills you have developed — both technical and interpersonal. Separate with commas.
                  </p>
                  <textarea
                    className="w-full rounded-lg border px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 transition-all"
                    style={{
                      borderColor: errors?.skills ? "var(--color-destructive)" : "var(--color-border)",
                      backgroundColor: "#fef3c7",
                      color: "var(--color-foreground)",
                      minHeight: "80px"
                    }}
                    placeholder="e.g., strategic planning, regulatory compliance, team leadership, budget management, stakeholder communication..."
                    value={formData?.skills}
                    onChange={handleChange("skills")}
                    rows={3} />
                  {errors?.skills && <p className="text-xs mt-1" style={{ color: "var(--color-destructive)", fontFamily: "var(--font-caption)" }}>{errors?.skills}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ fontFamily: "var(--font-heading)", color: "var(--color-foreground)", fontWeight: 700 }}>
                    Knowledge Domains
                  </label>
                  <p className="mb-2 text-sm" style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-caption)" }}>
                    What subjects do you know deeply — even outside your formal career?
                  </p>
                  <textarea
                    className="w-full rounded-lg border px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 transition-all"
                    style={{
                      borderColor: "var(--color-border)",
                      backgroundColor: "#fef3c7",
                      color: "var(--color-foreground)",
                      minHeight: "80px"
                    }}
                    placeholder="e.g., personal finance, nutrition, real estate investing, parenting teenagers, managing chronic illness..."
                    value={formData?.knowledge}
                    onChange={handleChange("knowledge")}
                    rows={3} />
                </div>
              </div>
            </div>

            {/* Interests */}
            <div className="bg-white rounded-2xl border p-6 md:p-8" style={{ border: "1.5px solid rgba(40,59,163,0.18)", backgroundColor: "#fffbf5" }}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: "rgba(40,59,163,0.08)" }}>
                  <Icon name="Heart" size={18} color="var(--color-primary)" strokeWidth={2} />
                </div>
                <h2 className="text-base md:text-lg font-bold" style={{ fontFamily: "var(--font-heading)", color: "var(--color-foreground)", fontWeight: 700 }}>Areas of Interest</h2>
              </div>
              <p className="mb-3 text-sm" style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-caption)" }}>
                Select all that resonate with you. These help identify income paths you will actually enjoy.
              </p>
              <div id="interests" className="flex flex-wrap gap-2">
                {INTEREST_OPTIONS?.map((interest) => {
                  const selected = formData?.interests?.includes(interest);
                  return (
                    <button
                      key={interest}
                      type="button"
                      onClick={() => toggleInterest(interest)}
                      className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200"
                      style={{
                        backgroundColor: selected ? "var(--color-primary)" : "#fef3c7",
                        borderColor: selected ? "var(--color-primary)" : "var(--color-border)",
                        color: selected ? "#fff" : "var(--color-foreground)",
                        fontFamily: "var(--font-caption)"
                      }}
                      aria-pressed={selected}>
                      {interest}
                    </button>);
                })}
              </div>
              {errors?.interests && <p className="text-xs mt-2" style={{ color: "var(--color-destructive)", fontFamily: "var(--font-caption)" }}>{errors?.interests}</p>}
            </div>

            {/* Problems */}
            <div className="bg-white rounded-2xl border p-6 md:p-8" style={{ border: "1.5px solid rgba(40,59,163,0.18)", backgroundColor: "#fffbf5" }}>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: "rgba(40,59,163,0.08)" }}>
                  <Icon name="Puzzle" size={18} color="var(--color-primary)" strokeWidth={2} />
                </div>
                <h2 className="text-base md:text-lg font-bold" style={{ fontFamily: "var(--font-heading)", color: "var(--color-foreground)", fontWeight: 700 }}>Problems You Enjoy Solving</h2>
              </div>
              <p className="mb-3 text-sm" style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-caption)" }}>
                What kinds of challenges energize you? What do people come to you for advice about?
              </p>
              <div id="problems">
                <textarea
                  className="w-full rounded-lg border px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 transition-all bg-amber-100"
                  style={{
                    borderColor: errors?.problems ? "var(--color-destructive)" : "var(--color-border)",
                    backgroundColor: "amber-100",
                    color: "",
                    fontFamily: "",
                    minHeight: "100px"
                  }}
                  placeholder="e.g., I enjoy helping organizations cut through bureaucracy to get things done. People ask me how to navigate complex systems, manage difficult teams, or turn around underperforming departments..."
                  value={formData?.problems}
                  onChange={handleChange("problems")}
                  rows={4} />
                {errors?.problems && <p className="text-xs mt-1" style={{ color: "var(--color-destructive)", fontFamily: "var(--font-caption)" }}>{errors?.problems}</p>}
              </div>
            </div>

            {/* CTA */}
            <div className="rounded-2xl p-6 md:p-8 border pb-8" style={{ background: "linear-gradient(135deg, rgba(40,59,163,0.06) 0%, rgba(211,172,53,0.06) 100%)", borderColor: "var(--color-border)" }}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base md:text-lg font-bold mb-1" style={{ fontFamily: "var(--font-heading)", color: "var(--color-foreground)", fontWeight: 700 }}>Ready to See Your Blueprint?</h3>
                  <p className="text-xs" style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-body)" }}>
                    Your personalized income strategy will be generated in seconds — no fluff, no hype, just a practical roadmap.
                  </p>
                </div>
                <Button
                  variant="default"
                  size="lg"
                  onClick={handleGenerate}
                  loading={isGenerating}
                  iconName={isGenerating ? undefined : "ArrowRight"}
                  iconPosition="right"
                  iconSize={18}
                  className="flex-shrink-0">
                  {isGenerating ? "Generating QuantuMe Prospectus..." : "Generate My QuantuMe Prospectus"}
                </Button>
              </div>
              
            </div>
 <div className="w-full flex justify-center text-center pb-0 px-[26px]">
          <p className="text-xs text-center mt-0" style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-body)" }}>
            By clicking "Generate My QuantuMe Prospectus Blueprint", you agree to our <a href="https://quantume.earth/terms" target="_blank" rel="noopener noreferrer" style={{ color: "var(--color-muted-foreground)" }}>Terms and Conditions</a> and <a href="https://quantume.earth/privacy-policy/" target="_blank" rel="noopener noreferrer" style={{ color: "var(--color-muted-foreground)" }}>Privacy &amp; Cookie</a> Policies.
          </p>
        </div>
          </div>
        </div>
      </main>
      {/* Footer */}
      <footer className="border-t mt-8" style={{ borderColor: "var(--color-border)", backgroundColor: "#fff8ed" }}>
        <div className="max-w-3xl mx-auto px-4 md:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 pl-0">
          <div className="flex items-center gap-2">
            <img
              src="/assets/images/logo-prospectus-rocket-nobg-1772749565916.png"
              alt="QuantuMe Prospectus Income Blueprint"
              className="h-6 w-auto" />

            <span className="text-sm font-semibold" style={{ fontFamily: "var(--font-heading)", color: "var(--color-primary)" }}>
              QuantuMe<span style={{ color: "var(--color-accent)" }}></span>
            </span>
          </div>
          <p className="text-xs text-right" style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-caption)" }}>
            &copy; {new Date()?.getFullYear()} <a href="https://quantume.earth/" target="_blank" rel="noopener noreferrer" style={{ color: "var(--color-muted-foreground)" }}>QuantuMe LLC</a>. All insights are for personal reflection and growth purposes. Not intended as psychological, medical, or financial advice.
          </p>
        </div>
      </footer>
      <BlueprintPaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onPaymentSuccess={handlePaymentSuccess}
        customerInfo={{ name: formData?.name, email: formData?.email }} />

    </div>);


};

export default ProfessionalInputForm;